import { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import { OfficeComment } from '@/models/common/OfficeComment';
import { StatusHistoryEntry } from '@/models/common/StatusHistoryEntry';
import { formatDateTimeLocale } from '@/helpers/dateHelper';
import deleteIcon from '@/assets/icons/delete.svg';
import styles from './OfficeCommentsDialog.module.css';

interface AppendClassificationOfficeCommentDtoOut {
  contactClassificationId: number;
  comment: string;
}

interface SendClassificationOfficeCommentDtoOut {
  contactClassificationId: number;
  commentIndex: number;
  isVerified: boolean;
  isFromStatusHistory: boolean;
}

interface SendOfficeCommentDtoIn {
  requestorEmail: string;
  emailSubject: string;
  emailHtmlContent: string;
}

interface DeleteClassificationOfficeCommentDtoOut {
  contactClassificationId: number;
  commentIndex: number;
  isFromStatusHistory: boolean;
  // The server deletes only if this is still the text at commentIndex.
  expectedComment: string;
}

// Combined comment from either source
interface CombinedComment {
  comment: OfficeComment;
  isFromStatusHistory: boolean;
  originalIndex: number;
}

interface OfficeCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  contactClassificationId: number;
  statusHistory: StatusHistoryEntry[];
  officeComments: OfficeComment[];
  onSuccess: () => void;
}

const OfficeCommentsDialog = ({
  open,
  onClose,
  contactClassificationId,
  statusHistory,
  officeComments,
  onSuccess,
}: OfficeCommentsDialogProps) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<SendOfficeCommentDtoIn | null>(null);
  const [pendingSend, setPendingSend] = useState<{ index: number; isFromStatusHistory: boolean } | null>(null);
  // The comment as it was when the office clicked delete - the list may refetch underneath the confirmation.
  const [pendingDelete, setPendingDelete] = useState<CombinedComment | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: appendComment, isPending: isAppending } = usePostAuthApi<void, AppendClassificationOfficeCommentDtoOut>(
    'management/classifications/office-comment'
  );

  const { mutate: sendComment, isPending: isSending } = usePutAuthApi<SendOfficeCommentDtoIn, object, SendClassificationOfficeCommentDtoOut>(
    'management/classifications/send-office-comment'
  );

  const { mutate: deleteComment, isPending: isDeleting } = usePostAuthApi<void, DeleteClassificationOfficeCommentDtoOut>(
    'management/classifications/office-comment/delete'
  );

  const isBusy = isAppending || isSending || isDeleting;

  // Combine comments from statusHistory and officeComments, sorted by date
  const combinedComments = useMemo(() => {
    const combined: CombinedComment[] = [];

    // Add comments from statusHistory (where officeComment exists)
    // Skip index 0 - it's a synthetic "הגשת בקשה" entry added in the DTO, not stored in DB
    statusHistory.forEach((entry, index) => {
      if (index === 0) return; // Skip synthetic entry
      if (entry.officeComment) {
        combined.push({
          comment: entry.officeComment,
          isFromStatusHistory: true,
          originalIndex: index - 1, // Adjust index to match backend's StatusHistory array
        });
      }
    });

    // Add standalone officeComments
    officeComments.forEach((comment, index) => {
      combined.push({
        comment,
        isFromStatusHistory: false,
        originalIndex: index,
      });
    });

    // Sort by date (oldest first)
    return combined.sort((a, b) => new Date(a.comment.date).getTime() - new Date(b.comment.date).getTime());
  }, [statusHistory, officeComments]);

  const getCommentKey = (isFromStatusHistory: boolean, index: number) => {
    return `${isFromStatusHistory ? 'sh' : 'oc'}-${index}`;
  };

  const refetchClassifications = () =>
    queryClient.refetchQueries({
      predicate: (query) => query.queryKey[0] === queryKeys.classifications.management[0],
    });

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;

    setDeleteError(null);
    setSuccessMessage(null);

    const body: DeleteClassificationOfficeCommentDtoOut = {
      contactClassificationId,
      commentIndex: pendingDelete.originalIndex,
      isFromStatusHistory: pendingDelete.isFromStatusHistory,
      expectedComment: pendingDelete.comment.comment,
    };

    deleteComment(body, {
      onSuccess: () => {
        setPendingDelete(null);
        setSuccessMessage(t('officeComments.commentDeleted'));
        refetchClassifications();
        onSuccess();
      },
      onError: (err: unknown) => {
        console.error('Error deleting office comment:', err);
        const axiosError = err as { response?: { status?: number } };
        // 409: the list changed since it was loaded, so the index no longer points at this comment.
        // Close the confirmation and reload the list - the office picks again from what is there now.
        if (axiosError.response?.status === 409) {
          setPendingDelete(null);
          setError(t('officeComments.commentsChanged'));
          refetchClassifications();
          onSuccess();
        } else {
          setDeleteError(t('officeComments.errorDeletingComment'));
        }
      },
    });
  };

  const handleAddComment = (andSend: boolean) => {
    if (!newComment.trim()) {
      setError(t('officeComments.commentRequired'));
      return;
    }

    setError(null);
    setSuccessMessage(null);

    const body: AppendClassificationOfficeCommentDtoOut = {
      contactClassificationId,
      comment: newComment.trim(),
    };

    appendComment(
      body,
      {
        onSuccess: () => {
          setNewComment('');
          setSuccessMessage(t('officeComments.commentAdded'));
          queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === queryKeys.classifications.management[0],
          });
          onSuccess();

          if (andSend) {
            // The new comment will be at the end of the officeComments list
            const newIndex = officeComments.length;
            handleSendComment(newIndex, false);
          }
        },
        onError: (err: unknown) => {
          console.error('Error adding office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorAddingComment'));
        },
      }
    );
  };

  const handleSendComment = (index: number, isFromStatusHistory: boolean) => {
    setError(null);
    setSuccessMessage(null);
    setSendingKey(getCommentKey(isFromStatusHistory, index));

    const body: SendClassificationOfficeCommentDtoOut = {
      contactClassificationId,
      commentIndex: index,
      isVerified: false, // First call is just for preview
      isFromStatusHistory,
    };

    sendComment(
      { body },
      {
        onSuccess: (data) => {
          setSendingKey(null);
          setPreviewData(data);
          setPendingSend({ index, isFromStatusHistory });
        },
        onError: (err: unknown) => {
          console.error('Error getting email preview:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingKey(null);
        },
      }
    );
  };

  const handleConfirmSend = () => {
    if (pendingSend === null) return;

    setError(null);
    setSendingKey(getCommentKey(pendingSend.isFromStatusHistory, pendingSend.index));

    const body: SendClassificationOfficeCommentDtoOut = {
      contactClassificationId,
      commentIndex: pendingSend.index,
      isVerified: true, // Actual send
      isFromStatusHistory: pendingSend.isFromStatusHistory,
    };

    sendComment(
      { body },
      {
        onSuccess: () => {
          setSendingKey(null);
          setPreviewData(null);
          setPendingSend(null);
          setSuccessMessage(t('officeComments.commentSent'));
          queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === queryKeys.classifications.management[0],
          });
          onSuccess();
        },
        onError: (err: unknown) => {
          console.error('Error sending office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingKey(null);
          setPreviewData(null);
          setPendingSend(null);
        },
      }
    );
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setPendingSend(null);
  };

  const handleClose = () => {
    setNewComment('');
    setError(null);
    setSuccessMessage(null);
    setPreviewData(null);
    setPendingSend(null);
    setPendingDelete(null);
    setDeleteError(null);
    onClose();
  };

  // Preview dialog
  if (previewData) {
    return (
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => !isOpen && handleCancelPreview()}
        title={t('officeComments.confirmSendTitle')}
      >
        <div className={styles.previewContent}>
          <p className="body-md-regular text-(--font-body-1)">
            {previewData.requestorEmail ? (
              <Trans
                i18nKey="officeComments.confirmSendMessageTo"
                values={{ email: previewData.requestorEmail }}
                components={{ email: <bdi dir="ltr" className="body-md-semibold" /> }}
              />
            ) : (
              t('officeComments.confirmSendMessage')
            )}
          </p>

          <div className={styles.emailPreview}>
            <div className={styles.emailSubject}>
              <strong>{t('officeComments.subject')}:</strong> {previewData.emailSubject}
            </div>
            <div className={styles.emailBody} dangerouslySetInnerHTML={{ __html: previewData.emailHtmlContent }} />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button className={styles.secondaryButton} onClick={handleCancelPreview} disabled={isSending}>
              {t('cancel')}
            </button>
            <button className={styles.primaryButton} onClick={handleConfirmSend} disabled={isSending}>
              {isSending ? t('loading') : t('officeComments.sendEmail')}
            </button>
          </div>
        </div>
      </MainDialogLayout>
    );
  }

  const renderCommentsTable = (comments: CombinedComment[]) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{t('officeComments.comment')}</th>
          <th>{t('officeComments.date')}</th>
          <th>{t('officeComments.createdBy')}</th>
          <th>{t('officeComments.sendStatus')}</th>
          <th aria-label={t('officeComments.deleteComment')} />
        </tr>
      </thead>
      <tbody>
        {comments.map((item) => {
          const key = getCommentKey(item.isFromStatusHistory, item.originalIndex);
          return (
            <tr key={key}>
              <td className={styles.noteCell} title={item.comment.comment}>
                {item.comment.comment}
              </td>
              <td className={styles.dateCell}>{formatDateTimeLocale(item.comment.date)}</td>
              <td>{item.comment.updatedBy || '-'}</td>
              <td className={styles.statusCell}>
                {item.comment.wasSent ? (
                  <div className={styles.sentInfo}>
                    <span>
                      {t('officeComments.sentOn')}: {item.comment.sendDate ? formatDateTimeLocale(item.comment.sendDate) : '-'}
                    </span>
                    <span>
                      {t('officeComments.sentBy')}: {item.comment.sentBy || '-'}
                    </span>
                  </div>
                ) : (
                  <button
                    className={styles.sendButton}
                    onClick={() => handleSendComment(item.originalIndex, item.isFromStatusHistory)}
                    disabled={sendingKey === key || isAppending}
                  >
                    {sendingKey === key ? t('loading') : t('officeComments.sendToRequestor')}
                  </button>
                )}
              </td>
              <td className={styles.deleteCell}>
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => {
                    setDeleteError(null);
                    setPendingDelete(item);
                  }}
                  disabled={isBusy}
                  title={t('officeComments.deleteComment')}
                  aria-label={t('officeComments.deleteComment')}
                >
                  <img src={deleteIcon} alt="" />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // Main dialog
  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()} title={t('officeComments.title')}>
      <div className={styles.content}>
        {/* Comments Table */}
        <div className={styles.tableSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.existingComments')}</h4>
          {combinedComments.length > 0 ? (
            renderCommentsTable(combinedComments)
          ) : (
            <div className={styles.noComments}>{t('officeComments.noComments')}</div>
          )}
        </div>

        {/* Add new note section */}
        <div className={styles.addNoteSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.addComment')}</h4>
          <textarea
            className={styles.textarea}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('officeComments.commentPlaceholder')}
            rows={3}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

        <div className={styles.actions}>
          <button className={styles.secondaryButton} onClick={handleClose} disabled={isBusy}>
            {t('close')}
          </button>
          <button
            className={styles.tertiaryButton}
            onClick={() => handleAddComment(true)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('officeComments.saveAndSend')}
          </button>
          <button
            className={styles.primaryButton}
            onClick={() => handleAddComment(false)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('save')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isDeleting) setPendingDelete(null);
        }}
        title={t('officeComments.confirmDeleteTitle')}
        message={pendingDelete?.comment.comment}
        confirmText="officeComments.deleteComment"
        cancelText="cancel"
        variant="danger"
        isLoading={isDeleting}
        error={deleteError}
        onConfirm={handleConfirmDelete}
      >
        {pendingDelete?.comment.wasSent && (
          <p className="body-sm-regular text-(--font-body-2)">{t('officeComments.deleteSentNote')}</p>
        )}
      </ConfirmationDialog>
    </MainDialogLayout>
  );
};

export default OfficeCommentsDialog;
